import { useCallback } from "react";
import { ApolloError } from "@apollo/client";
import { addError } from "@jsq/observability";
import { formatError } from "#common/hooks/useReportError";
import { ObjectType as PaymentObjectType, PaymentMethod } from "#graphql/supergraphEnums";
import { useCreatePayablePaymentBatchMutation } from "#graphql/supergraphHooks";
import { type CurrencyCode } from "#graphql/supergraphTypes";
import { clientContext, toDbId } from "#utils/graphql_utils";
import { paymentProcessDateToISO } from "../../DistributionWorkflow/StepComponents/ReviewAndSubmitStep/utils";

export interface UseCreatePayablePaymentBatchHandlerOptions {
  onSuccess?: (id: string) => void;
  onError?: (error: Error) => void;
}

export interface CreatePayablePaymentBatchParams {
  payableId: string; // global id
  payeeId: string; // global id
  amount: { value: number; precision: number; currency: CurrencyCode };
  paymentMethod: PaymentMethod | null;
  paymentDescription?: string | null;
  paymentDate?: Date | null;
}

export interface CreatePayablePaymentBatchResult {
  paymentBatchId?: string;
  error?: string;
}

export interface UseCreatePayablePaymentBatchHandlerReturn {
  createBatch: (
    params: CreatePayablePaymentBatchParams,
  ) => Promise<CreatePayablePaymentBatchResult>;
  loading: boolean;
  error: ApolloError | undefined;
}

/**
 * Hook that handles payment batch creation for payables.
 * Returns a result object with either paymentBatchId (on success) or error message (on failure).
 */
export const useCreatePayablePaymentBatchHandler = (
  options: UseCreatePayablePaymentBatchHandlerOptions = {},
): UseCreatePayablePaymentBatchHandlerReturn => {
  const { onSuccess, onError } = options;

  const [mutate, mutationMeta] = useCreatePayablePaymentBatchMutation({
    context: clientContext(),
    onError: (e) => {
      // Apollo invokes onError for every Apollo failure (network errors AND
      // GraphQL errors that surface via result.errors), so reporting here
      // covers all Apollo paths. The catch block below only fires for
      // unexpected non-Apollo errors and is gated to avoid double-firing.
      addError(formatError(e));
      onError?.(e);
    },
    refetchQueries: ["GetPayables"],
  });
  const loading = mutationMeta?.loading ?? false;
  const error = mutationMeta?.error;

  const createBatch = useCallback<UseCreatePayablePaymentBatchHandlerReturn["createBatch"]>(
    async (params): Promise<CreatePayablePaymentBatchResult> => {
      const { payableId, payeeId, amount, paymentMethod, paymentDescription, paymentDate } =
        params;

      if (!paymentMethod) {
        return { error: "Unable to determine payment method for payee" };
      }

      const parsed = amount.value;

      if (!Number.isFinite(parsed)) {
        return { error: "Enter a valid payment amount" };
      }

      const minorUnits = Math.round(parsed * Math.pow(10, amount.precision));
      if (minorUnits < 1) {
        return { error: "Payment amount must be at least $0.01" };
      }

      // Payment date is required by the GraphQL schema
      if (!paymentDate) {
        return { error: "Payment date is required" };
      }

      const scheduledPaymentProcessDate = paymentProcessDateToISO(paymentDate);
      if (!scheduledPaymentProcessDate) {
        return { error: "Invalid payment date" };
      }

      try {
        const result = await mutate({
          variables: {
            createPayablePaymentBatchInput: {
              objectId: toDbId(payableId),
              objectType: PaymentObjectType.Payable,
              paymentDescription: paymentDescription ?? undefined,
              scheduledPaymentProcessDate,
              payment: {
                objectId: toDbId(payeeId),
                objectType: PaymentObjectType.Payee,
                method: paymentMethod,
                amount: {
                  amount: minorUnits,
                  precision: amount.precision,
                  currency: amount.currency,
                },
              },
            },
          },
        });

        if (result.errors?.length) {
          const errMsg = result.errors.map((e: { message: string }) => e.message).join(", ");
          return { error: errMsg };
        }

        const createdId = result.data?.createPayablePaymentBatch?.id;
        if (!createdId) {
          return { error: "Server did not return a created payment batch id" };
        }

        onSuccess?.(createdId);
        return { paymentBatchId: createdId };
      } catch (e) {
        // Apollo errors are reported by the mutation's onError callback above;
        // only report unexpected non-Apollo errors here to avoid double-fire.
        if (!(e instanceof ApolloError)) {
          addError(formatError(e));
        }
        const message = e instanceof Error ? e.message : "Failed to create payment";
        return { error: message };
      }
    },
    [mutate, onSuccess],
  );

  return { createBatch, loading, error };
};
